import React from "react";
import { Link } from "react-router-dom";
import { POSTS } from "@/data/posts";
import { Reveal } from "@/components/Reveal";
import PageHead from "@/components/PageHead";
import { ArrowUpRight } from "@phosphor-icons/react";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

export default function BlogIndex() {
  const [feature, ...rest] = POSTS;

  return (
    <main data-testid="blog-page">
      <PageHead
        title="Insights"
        description="Editorial, playbooks and compliance breakdowns from the Citirex team. Performance marketing for the largest insurance and home-services verticals in the U.S."
        path="/insights"
      />
      <section className="border-b border-citirex-border bg-citirex-bg">
        <div className="mx-auto max-w-7xl px-6 pb-16 pt-12 md:px-12 md:pb-24 md:pt-20">
          <Reveal>
            <p className="overline">Insights</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-6 max-w-5xl font-display text-5xl font-semibold leading-[0.95] tracking-tighter md:text-7xl lg:text-[110px]">
              Notes from{" "}
              <span className="font-editorial italic font-normal text-citirex-accent">
                inside the funnel
              </span>
              .
            </h1>
          </Reveal>
        </div>
      </section>

      {feature && (
        <section className="bg-citirex-bg">
          <div className="mx-auto max-w-7xl px-6 py-16 md:px-12 md:py-24">
            <Link
              to={`/insights/${feature.slug}`}
              data-testid={`blog-feature-${feature.slug}`}
              className="group grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-16"
            >
              <div className="md:col-span-7">
                <div className="relative aspect-[16/10] w-full overflow-hidden">
                  <img
                    src={feature.cover}
                    alt={feature.title}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </div>
              <div className="md:col-span-5 md:py-6">
                <p className="overline">Featured · {feature.category}</p>
                <h2 className="mt-6 font-display text-4xl font-medium leading-[1.05] tracking-tight text-citirex-ink md:text-5xl">
                  {feature.title}
                </h2>
                <p className="mt-6 font-editorial text-xl italic leading-snug text-citirex-sub md:text-2xl">
                  {feature.excerpt}
                </p>
                <div className="mt-8 flex items-center justify-between border-t border-citirex-border pt-4">
                  <p className="font-body text-xs uppercase tracking-[0.2em] text-citirex-sub">
                    {feature.author} · {formatDate(feature.date)} ·{" "}
                    {feature.readTime}
                  </p>
                  <ArrowUpRight
                    size={20}
                    weight="bold"
                    className="text-citirex-ink transition-transform group-hover:rotate-45"
                  />
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {rest.length > 0 && (
        <section className="border-t border-citirex-border bg-citirex-bg">
          <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
            <p className="overline">All posts</p>
            <div className="mt-12 grid grid-cols-1 gap-px bg-citirex-border md:grid-cols-2 lg:grid-cols-3">
              {rest.map((p) => (
                <Link
                  key={p.slug}
                  to={`/insights/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="group flex flex-col bg-citirex-bg transition-colors hover:bg-citirex-surface"
                >
                  <div className="relative aspect-[16/10] w-full overflow-hidden">
                    <img
                      src={p.cover}
                      alt={p.title}
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex flex-1 flex-col gap-4 p-8">
                    <p className="font-body text-[10px] uppercase tracking-[0.25em] text-citirex-accent">
                      {p.category}
                    </p>
                    <h3 className="font-display text-2xl font-medium leading-tight text-citirex-ink">
                      {p.title}
                    </h3>
                    <p className="font-body text-sm text-citirex-sub">
                      {p.excerpt}
                    </p>
                    <div className="mt-auto flex items-center justify-between border-t border-citirex-border pt-4">
                      <p className="font-body text-[10px] uppercase tracking-[0.2em] text-citirex-sub">
                        {formatDate(p.date)} · {p.readTime}
                      </p>
                      <ArrowUpRight
                        size={16}
                        weight="bold"
                        className="transition-transform group-hover:rotate-45"
                      />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
